import {
    DotLegalAlert,
    DotLegalCheckbox,
    DotLegalDatePicker,
    DotLegalDialog,
    DotLegalHeader,
    DotLegalTable,
    DotLegalTextField,
} from "@dotlegal/dotlegal-ui-components";
import { Box, Collapse, TableCell, TableRow } from "@mui/material";
import { useTranslation } from "@localization/useTranslation";
import { useArchiveContractDialog, ContractObligationRow } from "./ArchiveContractDialog.hooks";
import { ITableHeader } from "@dotlegal/dotlegal-ui-components/dist/esm/types/components/dotLegalTable/DotLegalTable.types";
import DotLegalSection from "@common/components/dotLegalSection/DotLegalSection";
import { isEndDateBeforeStart } from "../ContractValidator.hooks.ts";

export interface IArchiveContractDialogProps {
    contractId: string;
    contractEndDate?: Date;
    contractStartDate?: Date;
    onDialogClose: () => void;
    onArchived: () => void;
}

function ArchiveContractDialog(props: IArchiveContractDialogProps) {
    const { translateString, getDateTimePickerLanguage } = useTranslation();
    const {
        endDate,
        setEndDate,
        reason,
        setReason,
        archiveContract,
        inProgress,
        obligations,
        isLoadingObligations,
        showObligations,
        onSelectAllObligations,
        isObligationChecked,
        onObligationCheckboxClick,
        getHeaderCheckBoxState,
    } = useArchiveContractDialog(props);

    const headers: Array<ITableHeader<ContractObligationRow>> = [{ text: translateString("name"), property: "name" }];
    const isEndDateBeforeOrEqualToStart = isEndDateBeforeStart(props.contractStartDate, endDate ?? undefined);
    return (
        <DotLegalDialog
            header={translateString("archiveContract")}
            buttonOkText={translateString("archive")}
            open
            onDialogClose={props.onDialogClose}
            onOkClick={archiveContract}
            inProgress={inProgress}
            isLoading={isLoadingObligations}
            size="md"
            okButtonDisabled={isEndDateBeforeOrEqualToStart}
        >
            <Box sx={{ display: "flex", justifyContent: "center" }}>
                <DotLegalHeader fontWeight={400} headerStyle="small">
                    {translateString("archiveContractSetEndDate")}
                </DotLegalHeader>
            </Box>
            <Box sx={{ marginBottom: 2 }}>
                <DotLegalDatePicker
                    label={translateString("endDate")}
                    value={endDate}
                    onChange={setEndDate}
                    language={getDateTimePickerLanguage()}
                    error={isEndDateBeforeOrEqualToStart}
                    helperMessage={isEndDateBeforeOrEqualToStart ? translateString("contractInvalidDateRange") : ""}
                />
            </Box>
            <DotLegalTextField label={translateString("reasonForArchiving")} value={reason} onChange={setReason} multiline rows={3} debounce={false} />
            <Collapse in={showObligations}>
                <DotLegalSection>
                    <DotLegalAlert severity="info" warningText={translateString("archiveContractObligationsInfo")} />
                    <DotLegalTable
                        headers={headers}
                        defaultOrderBy={"name"}
                        data={obligations}
                        isLoading={false}
                        hidePagination
                        headerCheckBoxState={getHeaderCheckBoxState()}
                        onSelectAllChanged={onSelectAllObligations}
                        renderRow={(row) => (
                            <TableRow key={row.id} onClick={() => onObligationCheckboxClick(row)}>
                                <TableCell>
                                    <DotLegalCheckbox margin={{ marginLeft: -1.5 }} checked={isObligationChecked(row.id)} />
                                </TableCell>
                                <TableCell>{row.name}</TableCell>
                            </TableRow>
                        )}
                    />
                </DotLegalSection>
            </Collapse>
        </DotLegalDialog>
    );
}

export default ArchiveContractDialog;
